import React from "react";
import OrderStatusBadge from "./OrderStatusBadge";
import OrderProgressBar from "./OrderProgressBar";
import { formatWeightFromGrams } from "../../../utils/weightUtils";
import { getColor } from "../../../utils/colorfunctions";
import { getCarrierTrackingUrl } from "../../../utils/carrierTracking";

function timeAgo(dateString) {
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
}

function getActionLabel(order) {
    const { status, user_role: userRole, fulfillment_method: fulfillmentMethod } = order;
    if (status === "requested" && userRole === "seller") return "Send Invoice";
    if (status === "requested" && userRole === "buyer") return "Awaiting Invoice";
    if (status === "invoice_sent" && userRole === "buyer") return "Pay Now";
    if (status === "invoice_sent" && userRole === "seller") return "Awaiting Payment";
    if (["paid", "handoff_partially_confirmed"].includes(status) && ["buyer_pickup", "seller_delivery"].includes(fulfillmentMethod)) {
        if (userRole === "seller") return "Awaiting Buyer";
        return fulfillmentMethod === "buyer_pickup" ? "Confirm Pickup" : "Confirm Delivery";
    }
    if (status === "paid" && userRole === "seller") return "Fulfill Order";
    if (status === "paid" && userRole === "buyer") return "Awaiting Fulfillment";
    if (status === "shipped" && userRole === "buyer") return "Confirm Receipt";
    if (status === "shipped" && userRole === "seller") return "Track Shipment";
    return null;
}
 
function isActionable(order) {
    const { status, user_role: userRole, fulfillment_method: fulfillmentMethod } = order;
    if (status === "requested" && userRole === "seller") return true;
    if (status === "invoice_sent" && userRole === "seller") return true;
    if (status === "invoice_sent" && userRole === "buyer") return true; 
    if (status === "paid" && userRole === "seller" && !fulfillmentMethod) return true;
    if (["paid", "handoff_partially_confirmed"].includes(status) && ["buyer_pickup", "seller_delivery"].includes(fulfillmentMethod)) {
        return userRole === "buyer";
    }
    if (status === "shipped" && userRole === "buyer") return true;
    return false; 
}

function getActionType(order) {
    const { status, user_role: userRole, fulfillment_method: fulfillmentMethod } = order;
    if (status === "requested" && userRole === "seller") return "send-invoice";
    if (status === "invoice_sent" && userRole === "buyer") return "notify-payment";
    if (status === "invoice_sent" && userRole === "seller") return "confirm-payment";
    if (status === "paid" && userRole === "seller" && !fulfillmentMethod) return "ship";
    return null;
}

const OrdersTable = ({
    orders,
    onSelectOrder,
    onAction,
    currentPage,
    totalPages,
    onPageChange,
    currentLicenseNo,
}) => {
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="orders-table-wrapper">
            <table className="orders-table">
                <thead>
                    <tr>
                        <th className="orders-th">Status</th>
                        <th className="orders-th">Product</th>
                        <th className="orders-th orders-th--hide-sm">Counterparty</th>
                        <th className="orders-th orders-th--hide-md">Amount</th>
                        <th className="orders-th orders-th--hide-lg">Progress</th>
                        <th className="orders-th orders-th--hide-md">Date</th>
                        <th className="orders-th orders-th--action">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((order) => {
                        const isSeller = order.seller_license_no === currentLicenseNo;
                        const companyName = isSeller ? order.buyer_company : order.seller_company;
                        const counterpartyEmail = isSeller
                            ? order.requester_email
                            : order.seller_email;
                        const actionLabel = getActionLabel(order);
                        const actionable = isActionable(order);
                        const trackingUrl = isSeller && order.status === "shipped" && order.fulfillment_method === "carrier_shipping"
                            ? getCarrierTrackingUrl(order.shipping_carrier, order.tracking_number)
                            : null;
                        const roleClass = isSeller ? "seller" : "buyer";

                        return (
                            <tr
                                key={order.id}
                                className={`orders-row orders-row--${roleClass}${order.needs_action ? " orders-row--attention" : ""}`}
                                onClick={() => onSelectOrder(order)}
                            >
                                <td className="orders-td">
                                    <OrderStatusBadge status={order.status} />
                                </td>
                                <td className="orders-td orders-td--product">
                                    <span className="orders-td__product-line">
                                        {order.product_color && order.product_color !== "no color" && ((order.product_type || "").toLowerCase() === "flower" || (order.product_type || "").toLowerCase() === "pre-roll") && (
                                            <span
                                                className="orders-color-dot"
                                                style={{ backgroundColor: getColor(order.product_color) }}
                                                title={order.product_color}
                                            />
                                        )}
                                        <span className="orders-td__product-name">
                                            {order.product_name || "Untitled"}
                                        </span>
                                    </span>
                                    <span className={`orders-role-mobile orders-role-mobile--${roleClass}`}>
                                        {isSeller ? "Selling" : "Buying"}
                                    </span>
                                </td>
                                <td className="orders-td orders-td--counterparty orders-th--hide-sm">
                                    <span className={`orders-role-label orders-role-label--${roleClass}`}>
                                        {isSeller ? "Selling to →" : "← Buying from"}
                                    </span>
                                    <span className="orders-td__company">
                                        {companyName || "—"}
                                    </span>
                                    {counterpartyEmail && (
                                        <span className="orders-td__email">
                                            {counterpartyEmail}
                                        </span>
                                    )}
                                </td>
                                <td className="orders-td orders-th--hide-md">
                                    {formatWeightFromGrams(order.weight_requested_grams)}
                                </td>
                                <td className="orders-td orders-th--hide-lg">
                                    <OrderProgressBar
                                        status={order.status}
                                        orderType={order.order_type}
                                    />
                                </td>
                                <td className="orders-td orders-th--hide-md orders-td--date">
                                    {timeAgo(order.updated_at || order.created_at)}
                                </td>
                                <td className="orders-td orders-td--action">
                                    {trackingUrl ? (
                                        <a
                                            className="orders-action-btn orders-action-btn--primary"
                                            href={trackingUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Track parcel (opens in new tab)
                                        </a>
                                    ) : actionLabel && (
                                        <button
                                            className={`orders-action-btn${actionable ? " orders-action-btn--primary" : ""}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const actionType = getActionType(order);
                                                if (actionType && onAction) {
                                                    onAction(actionType, order);
                                                } else {
                                                    onSelectOrder(order);
                                                }
                                            }}
                                        >
                                            {actionLabel}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="orders-pagination">
                    <button
                        className="orders-pagination__btn"
                        disabled={currentPage <= 1}
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    {startPage > 1 && (
                        <>
                            <button className="orders-pagination__btn" onClick={() => onPageChange(1)}>1</button>
                            {startPage > 2 && <span className="orders-pagination__ellipsis">&hellip;</span>}
                        </>
                    )}
                    {pageNumbers.map((num) => (
                        <button
                            key={num}
                            className={`orders-pagination__btn${num === currentPage ? " orders-pagination__btn--active" : ""}`}
                            onClick={() => onPageChange(num)}
                        >
                            {num}
                        </button>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <span className="orders-pagination__ellipsis">&hellip;</span>}
                            <button className="orders-pagination__btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                        </>
                    )}
                    <button
                        className="orders-pagination__btn"
                        disabled={currentPage >= totalPages}
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrdersTable;
